/**
 * O estado do canal de WhatsApp no painel — o bloco de tipo `canal` (#443, §3.1).
 *
 * Era JSX solto dentro de `HomePage`, alimentado por um campo fixo do contrato. Virou
 * componente porque o contrato virou lista: o canal agora é um bloco como os outros, e quem
 * escolhe o componente é o `tipo`.
 *
 * O vocabulário visual é o dos vizinhos (`AttendanceFunnel`, `ScheduleOccupancyCard`): título
 * em `h6` com o ícone do assunto, o resumo à direita, e uma única ação, no rodapé.
 */
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Circle as CircleIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';

import type { WhatsAppDashboardStatus } from '../../../types/dashboard';

interface CanalDeWhatsAppProps {
  rotulo: string;
  data: WhatsAppDashboardStatus;
  onGerenciar?: () => void;
}

const VERDE_DO_WHATSAPP = '#25D366';

const CanalDeWhatsApp: React.FC<CanalDeWhatsAppProps> = ({ rotulo, data, onGerenciar }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WhatsAppIcon sx={{ color: VERDE_DO_WHATSAPP, fontSize: 24 }} />
        <Typography variant="h6">{rotulo}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {data.connectedCount}/{data.totalCount} conectados
        </Typography>
      </Box>

      <List dense disablePadding>
        {data.instances.map((instancia) => (
          <ListItem key={instancia.id} disablePadding sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 24 }}>
              <CircleIcon
                sx={{
                  fontSize: 10,
                  color: instancia.status === 'connected' ? 'success.main' : 'grey.400',
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={instancia.name}
              secondary={instancia.phone || 'Não conectado'}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        ))}
      </List>
    </CardContent>

    {onGerenciar && (
      <CardActions>
        <Button size="small" onClick={onGerenciar}>
          Gerenciar
        </Button>
      </CardActions>
    )}
  </Card>
);

export default CanalDeWhatsApp;
